import React, { useEffect, useRef, useState } from 'react';

/**
 * Massing model of the actual plan: a central core holding the entrance,
 * kitchen and washroom, with the two bedroom wings splayed off it at an
 * angle, a pergola over the entrance deck, and the pool alongside.
 * Built from transformed divs — a few KB instead of a WebGL runtime.
 */

const Box = ({ className = '', w, h, d, x = 0, y = 0, z = 0, ry = 0, children }) => (
  <div
    className={`vol ${className}`}
    style={{
      '--w': `${w}px`,
      '--h': `${h}px`,
      '--d': `${d}px`,
      transform: `translate3d(${x}px, ${y}px, ${z}px) rotateY(${ry}deg)`,
    }}
  >
    <div className="f front">{children}</div>
    <div className="f back" />
    <div className="f right" />
    <div className="f left" />
    <div className="f top" />
  </div>
);

const ContainerModel = () => {
  const rigRef = useRef(null);
  const sceneRef = useRef(null);
  const [hint, setHint] = useState(true);

  useEffect(() => {
    const rig = rigRef.current;
    const scene = sceneRef.current;
    if (!rig || !scene) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let rotation = -28;
    let dragging = false;
    let lastX = 0;
    let velocity = 0;
    let raf = 0;
    let visible = true;
    const autoSpeed = reduce ? 0 : 0.08;

    const apply = () => {
      rig.style.transform = `rotateX(-24deg) rotateY(${rotation}deg)`;
    };

    const tick = () => {
      if (!dragging) {
        rotation += autoSpeed + velocity;
        velocity *= 0.94;
      }
      apply();
      if (visible) raf = requestAnimationFrame(tick);
    };

    apply();
    raf = requestAnimationFrame(tick);

    const onDown = (e) => {
      dragging = true;
      lastX = e.clientX;
      velocity = 0;
      setHint(false);
      scene.classList.add('is-dragging');
      scene.setPointerCapture(e.pointerId);
    };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      lastX = e.clientX;
      rotation += dx * 0.45;
      velocity = dx * 0.05;
    };
    const onUp = () => {
      dragging = false;
      scene.classList.remove('is-dragging');
    };

    scene.addEventListener('pointerdown', onDown);
    scene.addEventListener('pointermove', onMove);
    scene.addEventListener('pointerup', onUp);
    scene.addEventListener('pointercancel', onUp);

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) raf = requestAnimationFrame(tick);
      else cancelAnimationFrame(raf);
    });
    io.observe(scene);

    return () => {
      cancelAnimationFrame(raf);
      scene.removeEventListener('pointerdown', onDown);
      scene.removeEventListener('pointermove', onMove);
      scene.removeEventListener('pointerup', onUp);
      scene.removeEventListener('pointercancel', onUp);
      io.disconnect();
    };
  }, []);

  return (
    <div className="model-scene" ref={sceneRef}>
      <div className="model-glow" aria-hidden="true" />
      <div className="model-rig" ref={rigRef}>
        {/* site */}
        <div className="plane lawn" />
        <div className="plane pool" />
        <div className="plane deck" />

        {/* core — entrance, kitchen, washroom */}
        <Box className="core" w={104} h={62} d={96} x={-40} y={-31} z={0}>
          <span className="glass g-a" />
          <span className="door" />
        </Box>

        {/* service strip: kitchen + washroom along the core's far side */}
        <Box className="service" w={104} h={58} d={36} x={-40} y={-29} z={-66} />

        {/* bedroom wings, splayed off the core */}
        <Box className="wing" w={148} h={56} d={72} x={96} y={-28} z={-58} ry={-34}>
          <span className="glass g-b" />
          <span className="glass g-c" />
        </Box>
        <Box className="wing" w={148} h={56} d={72} x={96} y={-28} z={62} ry={34}>
          <span className="glass g-b" />
          <span className="glass g-c" />
        </Box>

        {/* pergola over the entrance deck */}
        <div className="pergola">
          <span className="post p1" />
          <span className="post p2" />
          <span className="post p3" />
          <span className="post p4" />
          <span className="slats" />
        </div>
      </div>
      <div className="model-shadow" aria-hidden="true" />
      {hint && <span className="model-hint">Drag to turn</span>}
      <div className="model-key">
        <span><i className="sw core" /> Entrance &middot; kitchen &middot; washroom</span>
        <span><i className="sw wing" /> Bedroom wings</span>
        <span><i className="sw water" /> Pool</span>
      </div>
    </div>
  );
};

export default ContainerModel;
